import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Scenarios = () => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState('ecommerce');

  const tabs = [
    { key: 'ecommerce', icon: 'ri-shopping-cart-line' },
    { key: 'saas', icon: 'ri-cloud-line' },
    { key: 'gaming', icon: 'ri-gamepad-line' },
    { key: 'otc', icon: 'ri-exchange-line' }
  ];

  const scenarioImages = {
    ecommerce: "/images/scenario-placeholder.png",
    saas: "/images/scenario-placeholder.png",
    gaming: "/images/scenario-placeholder.png",
    otc: "/images/scenario-placeholder.png"
  };

  return (
    <section id="scenarios" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_35%,rgba(6,182,212,0.1)_50%,transparent_65%)]"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('scenarios.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {t('scenarios.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Tabs */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-1 gap-3 lg:gap-4 lg:sticky lg:top-32">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex flex-col sm:flex-row lg:flex-row items-center justify-center sm:justify-start lg:justify-start p-4 sm:p-6 rounded-2xl text-center sm:text-left lg:text-left transition-all duration-300 ${
                    activeTab === tab.key
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                      : 'bg-slate-800/50 border border-slate-700/50 text-gray-300 hover:border-cyan-500/30 hover:bg-slate-700/50'
                  }`}
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-2 sm:mb-0 sm:mr-4 lg:mr-4 flex-shrink-0 ${
                    activeTab === tab.key 
                      ? 'bg-white/20' 
                      : 'bg-gradient-to-r from-cyan-400 to-blue-500'
                  }`}>
                    <i className={`${tab.icon} text-lg sm:text-xl ${
                      activeTab === tab.key ? 'text-white' : 'text-white'
                    }`}></i>
                  </div>
                  <span className="text-sm sm:text-base lg:text-lg font-semibold">
                    {t(`scenarios.tabs.${tab.key}`)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-3xl border border-slate-700/50 backdrop-blur-sm p-6 sm:p-8 lg:p-12">
              {/* Image */}
              <div className="mb-6 sm:mb-8 rounded-2xl overflow-hidden">
                <img 
                  src={scenarioImages[activeTab as keyof typeof scenarioImages]}
                  alt={t(`scenarios.content.${activeTab}.title`)}
                  className="w-full h-48 sm:h-64 lg:h-64 object-cover"
                />
              </div>

              {/* Content */}
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-2xl sm:text-3xl font-bold text-white">
                  {t(`scenarios.content.${activeTab}.title`)}
                </h3>
                
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed">
                  {t(`scenarios.content.${activeTab}.description`)}
                </p>

                {/* Points */}
                <div className="space-y-3 sm:space-y-4">
                  {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} className="flex items-start space-x-2 sm:space-x-3">
                      <div className="w-6 h-6 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-sm text-white"></i>
                      </div>
                      <span className="text-sm sm:text-base text-gray-300">
                        {t(`scenarios.content.${activeTab}.points.${i}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Scenarios;